import { createContext, useContext } from "react";

import { useLabAssetItem } from "../labAssets.query";
import type { LabAsset } from "../labAssets.types";

export type LabAssetContextType = {
  data: LabAsset | undefined;
  // isPending, not isLoading: an offline-paused query (networkMode "online")
  // has isLoading false and isError false with no data yet, and would fall
  // through a loading guard to the not-found state before any request ran.
  isPending: boolean;
  isFetched: boolean;
  isError: boolean;
  refetch: () => void;
};

export type LabAssetContextProviderProps = {
  children: React.ReactNode;
  uuid: string;
};

const LabAssetContext = createContext<LabAssetContextType | undefined>(
  undefined
);

// Read-only: edits go through EditLabAssetModal (ENG-1670), whose mutation
// invalidates labAssetQueryKeys.item(uuid) so this provider refetches.
// useLabAssetItem maps a true 404 to null, surfaced here as undefined so
// consumers get one falsy not-found signal; every other failure surfaces as
// isError (retryable via refetch), never as not-found.
export const LabAssetContextProvider: React.FC<LabAssetContextProviderProps> =
  ({ children, uuid }) => {
    const { data, isPending, isFetched, isError, refetch } =
      useLabAssetItem(uuid);

    const contextValue: LabAssetContextType = {
      data: data ?? undefined,
      isPending,
      isFetched,
      isError,
      refetch,
    };

    return (
      <LabAssetContext.Provider value={contextValue}>
        {children}
      </LabAssetContext.Provider>
    );
  };

// Returns undefined outside the provider — consumers do their own null check,
// matching the useEvidenceContext convention.
export const useLabAssetContext = () => {
  const context = useContext(LabAssetContext);
  return context;
};
